<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import {
  FetchCommentResponseAccessLevel,
  type FetchCommentResponse,
  type CreateCommentDTOAccessLevel,
} from 'src/api/models';
import { usePostCreateComment, usePostFetchAllComments } from 'src/api/queries/order-portal-bff';
import { useUser } from 'src/composables/useUser';
import useDateTime from 'src/composables/useDateTime';
import TicketEditSectionHeader from './TicketEditSectionHeader.vue';

const props = defineProps<{ ticketId: string }>();

const { userId } = useUser();
const { formatRelativeTime, formatDateTime } = useDateTime();

const {
  data: commentsResponse,
  mutateAsync: fetchComments,
  isPending: isLoadingComments,
} = usePostFetchAllComments();

const {
  mutateAsync: postCreateComment,
  isPending: isCreatingComment,
} = usePostCreateComment();

const currentPage = ref(1);
const itemsPerPage = ref(10);
const totalPages = ref(0);
const newComment = ref('');
const selectedVisibility = ref<FetchCommentResponseAccessLevel>(
  FetchCommentResponseAccessLevel.internal,
);

const comments = computed<FetchCommentResponse[]>(() => (commentsResponse.value as any)?.data ?? []);

const isPrivate = computed(
  () => selectedVisibility.value === FetchCommentResponseAccessLevel.internal,
);

const visibilityLabel = computed(() => (isPrivate.value ? 'private' : 'public'));

async function fetchCommentsList(page = 1) {
  try {
    const response = await fetchComments({
      params: { page, limit: itemsPerPage.value },
      data: { ticket_id: props.ticketId, access_level: selectedVisibility.value as any },
    });
    if (response) {
      totalPages.value = (response as any).total_pages || 0;
    }
  } catch (error) {
    console.error('Failed to fetch comments:', error);
  }
}

function goToPage(direction: 'prev' | 'next') {
  if (direction === 'prev' && currentPage.value > 1) {
    currentPage.value--;
    fetchCommentsList(currentPage.value);
  } else if (direction === 'next' && currentPage.value < totalPages.value) {
    currentPage.value++;
    fetchCommentsList(currentPage.value);
  }
}

async function addComment() {
  if (!newComment.value.trim()) return;
  try {
    await postCreateComment({
      data: {
        ticket_id: props.ticketId,
        content: newComment.value,
        access_level: selectedVisibility.value as unknown as CreateCommentDTOAccessLevel,
        author_id: userId.value,
      },
    });
    newComment.value = '';
    await fetchCommentsList(currentPage.value);
  } catch (error) {
    console.error('Failed to add comment:', error);
  }
}

function getRelativeTime(dateString?: string): string {
  if (!dateString) return '';
  return formatRelativeTime(new Date(dateString + 'Z'));
}

function getFullDateTime(dateString?: string): string {
  if (!dateString) return '';
  return formatDateTime(new Date(dateString + 'Z'));
}

function getInitials(author?: { first_name?: string; last_name?: string }): string {
  return ((author?.first_name?.[0] ?? '') + (author?.last_name?.[0] ?? '')).toUpperCase() || '?';
}

function getAuthorName(author?: { first_name?: string; last_name?: string }): string {
  return [author?.first_name, author?.last_name].filter(Boolean).join(' ') || 'Unknown';
}

watch(selectedVisibility, () => {
  currentPage.value = 1;
  fetchCommentsList(1);
});

onMounted(() => fetchCommentsList(1));
</script>

<template>
  <div class="p-8">
    <TicketEditSectionHeader title="Comments" class="mb-6">
      <template #actions>
        <div class="visibility-toggle">
        <button
          class="toggle-btn"
          :class="{ 'toggle-active': isPrivate }"
          @click="selectedVisibility = FetchCommentResponseAccessLevel.internal"
        >
          <i class="pi pi-lock" style="font-size: 11px" />
          Private
        </button>
        <button
          class="toggle-btn"
          :class="{ 'toggle-active': !isPrivate }"
          @click="selectedVisibility = FetchCommentResponseAccessLevel.public"
        >
          <i class="pi pi-globe" style="font-size: 11px" />
          Public
        </button>
      </div>
      </template>
    </TicketEditSectionHeader>

    <div v-if="isLoadingComments" class="flex items-center justify-center py-12">
      <i class="pi pi-spinner pi-spin text-surface-400" style="font-size: 20px" />
    </div>

    <template v-else>
      <div class="flex flex-col gap-3 mb-6">
        <div
          v-for="note in comments"
          :key="note.id"
          class="comment-card"
        >
          <div class="flex items-start gap-3">
            <div class="comment-avatar">
              {{ getInitials(note.author) }}
            </div>
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 mb-1">
                <span class="text-sm font-semibold text-surface-800 dark:text-surface-200">
                  {{ getAuthorName(note.author) }}
                </span>
                <span
                  class="visibility-badge"
                  :class="note.access_level === FetchCommentResponseAccessLevel.internal ? 'badge-private' : 'badge-public'"
                >
                  {{ note.access_level === FetchCommentResponseAccessLevel.internal ? 'Private' : 'Public' }}
                </span>
                <span
                  class="text-xs text-surface-400 ml-auto whitespace-nowrap"
                  :title="getFullDateTime(note.created_at)"
                >
                  {{ getRelativeTime(note.created_at) }}
                </span>
              </div>
              <p class="text-sm text-surface-600 dark:text-surface-400 whitespace-pre-wrap leading-relaxed select-text">
                {{ note.content }}
              </p>
            </div>
          </div>
        </div>

        <div v-if="comments.length === 0" class="empty-state">
          <i class="pi pi-comments" style="font-size: 28px" />
          <p>No {{ visibilityLabel }} comments yet</p>
        </div>
      </div>

      <div v-if="totalPages > 1" class="flex items-center justify-center gap-3 mb-6">
        <button
          class="page-btn"
          :disabled="currentPage <= 1"
          @click="goToPage('prev')"
        >
          <i class="pi pi-chevron-left" style="font-size: 11px" />
        </button>
        <span class="text-xs text-surface-500">
          Page {{ currentPage }} of {{ totalPages }}
        </span>
        <button
          class="page-btn"
          :disabled="currentPage >= totalPages"
          @click="goToPage('next')"
        >
          <i class="pi pi-chevron-right" style="font-size: 11px" />
        </button>
      </div>

      <div class="comment-form">
        <textarea
          v-model="newComment"
          class="comment-textarea"
          :placeholder="`Write a ${visibilityLabel} comment...`"
          rows="3"
        />
        <div class="flex items-center justify-between mt-2">
          <span class="text-xs text-surface-400">
            Posting as {{ visibilityLabel }}
          </span>
          <button
            class="send-btn"
            :disabled="!newComment.trim() || isCreatingComment"
            @click="addComment"
          >
            <i v-if="isCreatingComment" class="pi pi-spinner pi-spin" style="font-size: 12px" />
            <i v-else class="pi pi-send" style="font-size: 12px" />
            Add Comment
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.visibility-toggle {
  display: inline-flex;
  border-radius: 0.5rem;
  border: 1px solid var(--p-surface-200);
  overflow: hidden;
}
:root[class*='app-dark'] .visibility-toggle {
  border-color: var(--p-surface-700);
}
.toggle-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--p-surface-500);
  background: transparent;
  transition: all 0.15s;
  cursor: pointer;
}
.toggle-btn:hover {
  color: var(--p-surface-700);
  background: var(--p-surface-50);
}
:root[class*='app-dark'] .toggle-btn:hover {
  color: var(--p-surface-300);
  background: var(--p-surface-800);
}
.toggle-active {
  color: var(--p-primary-700);
  background: var(--p-primary-50);
  font-weight: 600;
}
:root[class*='app-dark'] .toggle-active {
  color: var(--p-primary-300);
  background: color-mix(in srgb, var(--p-primary-500) 15%, transparent);
}

.comment-card {
  padding: 0.875rem 1rem;
  border-radius: 0.75rem;
  border: 1px solid var(--p-surface-200);
  background: var(--p-surface-0);
  transition: border-color 0.15s;
}
:root[class*='app-dark'] .comment-card {
  border-color: var(--p-surface-700);
  background: var(--p-surface-800);
}

.comment-avatar {
  width: 2rem;
  height: 2rem;
  border-radius: 9999px;
  background: linear-gradient(135deg, var(--p-primary-400), var(--p-primary-600));
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  color: white;
  flex-shrink: 0;
}

.visibility-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.0625rem 0.4375rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.badge-private {
  background: var(--p-amber-50);
  color: var(--p-amber-600);
}
:root[class*='app-dark'] .badge-private {
  background: color-mix(in srgb, var(--p-amber-500) 15%, transparent);
  color: var(--p-amber-400);
}
.badge-public {
  background: var(--p-blue-50);
  color: var(--p-blue-600);
}
:root[class*='app-dark'] .badge-public {
  background: color-mix(in srgb, var(--p-blue-500) 15%, transparent);
  color: var(--p-blue-400);
}


.page-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 0.375rem;
  border: 1px solid var(--p-surface-200);
  color: var(--p-surface-500);
  background: white;
  transition: all 0.15s;
  cursor: pointer;
}
:root[class*='app-dark'] .page-btn {
  border-color: var(--p-surface-700);
  background: var(--p-surface-800);
  color: var(--p-surface-400);
}
.page-btn:hover:not(:disabled) {
  border-color: var(--p-primary-300);
  color: var(--p-primary-500);
}
.page-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.comment-form {
  border-top: 1px solid var(--p-surface-200);
  padding-top: 1rem;
}
:root[class*='app-dark'] .comment-form {
  border-color: var(--p-surface-700);
}

.comment-textarea {
  width: 100%;
  padding: 0.75rem 1rem;
  border-radius: 0.75rem;
  border: 1px solid var(--p-surface-200);
  background: white;
  color: var(--p-surface-700);
  font-size: 0.875rem;
  line-height: 1.6;
  min-height: 80px;
  outline: none;
  transition: all 0.15s;
  resize: vertical;
  font-family: inherit;
}
:root[class*='app-dark'] .comment-textarea {
  border-color: var(--p-surface-700);
  background: var(--p-surface-800);
  color: var(--p-surface-300);
}
.comment-textarea:hover {
  border-color: var(--p-primary-300);
}
:root[class*='app-dark'] .comment-textarea:hover {
  border-color: var(--p-primary-600);
}
.comment-textarea:focus {
  border-color: var(--p-primary-400);
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--p-primary-500) 10%, transparent);
}
.comment-textarea::placeholder {
  color: var(--p-surface-400);
}

.send-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: white;
  background: var(--p-primary-500);
  transition: all 0.15s;
  cursor: pointer;
}
.send-btn:hover:not(:disabled) {
  background: var(--p-primary-600);
}
.send-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
</style>
